import React, { ReactElement, useEffect, useState } from 'react';
import {
  ActivityIndicator, Platform, Pressable, StyleSheet, Text,
  TextInput, TouchableOpacity, View,
} from 'react-native';
import { useSelector } from 'react-redux';
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetScrollView } from '@gorhom/bottom-sheet';
import Colors from '../../constants/Colors';
import CloseModalIcon from '../CloseModalIcon';
import UploadIcon from '../24pxIcons/UploadIcon';
import SearchIcon from '../SearchIcon';
import updateUserGroup from '../../requests/Users/UpdateUserGroup';
import updateUserGroupUsers from '../../requests/Users/UpdateUserGroupUsers';
import AlertModal from '../AlertModal';
import addUserGroup from '../../requests/Users/AddUserGroup';
import BinIcon24 from '../24pxIcons/BinIcon24';
import deleteUserGroup from '../../requests/Users/DeleteUserGroup';
import { logEvent } from '../LogEvent';
import {
  FEATURE_COMING_SOON, USER_GROUP_UPDATE_FAILURE, USER_LIST_UPDATE_FAILURE,
} from '../../constants/AlertMessages';
import { UserPermission } from '../../constants/Types';
import isMobile from '../IsMobile';
import nameRegex from '../Regex';
import TabButton from '../TabButton';
import GroupUserRowMobile from './GroupUserRowMobile';
import CheckBox from '../16pxIcons/CheckBox';
import CustomTextField from '../CustomTextField';

interface Props {
  title: any;
  description: any;
  users: any;
  groupId: any;
  // eslint-disable-next-line no-unused-vars
  updatedGroup(groupId: string, newTitle: string, newDesc: string, count: number): void;
  groupDeleted(): void;
  notificationGroups;
  close(): void;
  userGroupSheetRef?: React.RefObject<BottomSheetModal>;
}

const UserGroupBottomSheet = (props: Props) => {
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_UPDATE);
  const canDeleteUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_DELETE);
  const [groupUserSearchValue, setAddGroupUserSearchValue] = useState<string>('');
  const [nameValue, setNameValue] = useState<string>(props.title);
  const [descriptionValue, setDescriptionValue] = useState<string>(props.description);
  const [users, setUsers] = useState(props.users ?? []);
  const [saving, setSaving] = useState<boolean>(false);
  const [titleValid, setTitleValid] = useState<boolean>(props.title?.trim().length > 0);

  const maxTitleLength = 40;

  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertActionTitle, setAlertActionTitle] = useState<string | null>(null);
  const [alertAction, setAlertAction] = useState<() => void | null>(null);
  const [selectedTab, setSelectedTab] = useState<'ESSENTIAL_INFO' | 'MEMBERS'>('ESSENTIAL_INFO');

  const saveButtonDisabled: boolean = !titleValid;

  useEffect(() => {
    setNameValue(props.title);
  }, [props.title]);

  useEffect(() => {
    setDescriptionValue(props.description);
  }, [props.description]);

  useEffect(() => {
    setUsers(props.users ?? []);
  }, [props.users]);

  const showAlert = (
    title: string,
    error: boolean = true,
    message: string | ReactElement = '',
    actionTitle: string | null = null,
    action: () => void | null = null,
  ) => {
    setAlertTitle(title);
    setAlertError(error);
    setAlertMessage(message);
    setAlertActionTitle(actionTitle);
    setAlertAction(() => action);
    setSaving(false);
    setAlertVisible(true);
  };

  const restoreDefaults = () => {
    setAddGroupUserSearchValue('');
    setNameValue(props.title);
    setDescriptionValue(props.description);
    const usersCopy = Object.assign([], props.users);
    if (props.title === undefined) {
      // eslint-disable-next-line no-return-assign
      usersCopy.forEach((user) => user.selected = false);
    }
    setUsers(usersCopy);
    setSaving(false);
    setTitleValid(props.title?.trim().length > 0);
  };

  React.useEffect(() => {
    setUsers(props.users ?? []);
  }, [props.users]);

  function validateTitle(title) {
    let valid = true;
    if (
      title === undefined
      || title === null
      || title?.trim().length === 0
      || title?.trim().length > maxTitleLength
      || nameRegex.exp.test(title?.trim()) === false
    ) {
      valid = false;
      setTitleValid(false);
    } else if (
      props.title?.toLocaleLowerCase() !== 'admin'
      && title?.trim().toLocaleLowerCase() === 'admin'
    ) {
      valid = false;
      setTitleValid(false);
    } else {
      setTitleValid(true);
    }
    return valid;
  }

  const save = () => {
    logEvent('UserGroupBottomSheet', 'Save');
    if (saveButtonDisabled) return;
    setSaving(true);
    const userIds = [];
    const deletedUserIds = [];
    users.forEach((user) => {
      if (user.selected) {
        userIds.push(user.id);
      } else if (user.deleted) {
        deletedUserIds.push(user.id);
      }
    });
    if (props.title !== undefined) {
      updateUserGroup(props.groupId, nameValue?.trim(), descriptionValue).then(
        (response) => {
          if (response && !response.isError) {
            updateUserGroupUsers(
              props.groupId,
              userIds,
              deletedUserIds,
              props.title,
              buildingId,
            ).then(
              (res) => {
                if (res?.isError) {
                  showAlert(
                    'Error',
                    true,
                    USER_LIST_UPDATE_FAILURE,
                  );
                } else {
                  setSaving(false);
                  props.updatedGroup(
                    props.groupId,
                    nameValue?.trim(),
                    descriptionValue,
                    users.filter(
                      (user) => user.selected
                        ?? (user.userGroupIds ?? []).indexOf(props.groupId) > -1,
                    ).length,
                  );
                }
              },
            );
          } else {
            showAlert(
              'Error',
              true,
              response.errorText ?? USER_GROUP_UPDATE_FAILURE,
            );
          }
        },
      );
    } else {
      addUserGroup(nameValue?.trim(), descriptionValue ?? '', userIds).then((res) => {
        if (res && !res.isError) {
          setSaving(false);
          props.updatedGroup(
            props.groupId,
            nameValue?.trim(),
            descriptionValue,
            users.filter(
              (user) => user.selected ?? (user.userGroupIds ?? [])
                .indexOf(props.groupId) > -1,
            ).length,
          );
        } else showAlert('Error', true, res?.errorText);
      });
    }
    setAddGroupUserSearchValue('');
  };

  const renderGroupDetails = () => (
    <View style={{ marginHorizontal: 24 }}>
      <Text
        style={{
          marginBottom: 16,
          fontSize: 16,
          fontFamily: 'Poppins_400Regular',
          color: Colors.light.hereworksBlack400,
        }}
      >
        Fields marked with * are mandatory
      </Text>
      <View style={{ flexDirection: 'row' }}>
        <TouchableOpacity
          style={{
            height: 64,
            width: 64,
            backgroundColor: Colors.light.hereworksDarkBlue,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 32,
          }}
          onPress={() => {
            logEvent('UserGroupBottomSheet', 'Upload_Icon');
            showAlert(FEATURE_COMING_SOON, false);
          }}
        >
          <UploadIcon />
        </TouchableOpacity>
        <View style={{ justifyContent: 'center', marginLeft: 16 }}>
          <Text
            style={{
              marginBottom: 8,
              fontFamily: 'Roboto_400Regular',
              fontSize: 16,
              color: Colors.light.hereworksBlack600,
            }}
          >
            Upload an image
          </Text>
          <TouchableOpacity
            onPress={() => {
              logEvent('UserGroupBottomSheet', 'Upload');
              showAlert(FEATURE_COMING_SOON, false);
            }}
          >
            <Text
              style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                color: Colors.light.hereworksBlue500,
              }}
            >
              Upload
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      <Text style={styles.textBoxHeaders}>Group name*</Text>
      <CustomTextField
        value={nameValue}
        updateValue={(text) => {
          validateTitle(text);
          setNameValue(text);
        }}
        placeholder={'Group name'}
      />
      {nameValue?.trim().length > maxTitleLength && (
        <Text style={[styles.validationText, { color: Colors.light.hereworksUrgentRed }]}>
          {`${nameValue?.trim().length ?? props.title?.trim().length ?? 0}/${maxTitleLength}`}
        </Text>
      )}
      <Text style={styles.textBoxHeaders}>Description</Text>
      <CustomTextField
        value={descriptionValue}
        updateValue={setDescriptionValue}
        placeholder={'Add a description for this group (optional)'}
        multiline
      />
    </View>
  );

  const renderGroupMembers = () => (
    <View
      style={{
        marginLeft: isMobile ? 16 : 32,
        marginRight: isMobile ? 16 : 0,
        flexGrow: 1,
      }}
    >
      <Text
        style={{
          marginBottom: 4,
          fontSize: 16,
          fontFamily: 'Poppins_500Medium',
          color: Colors.light.hereworksBlack600,
        }}
      >
        Select members
      </Text>
      <Text style={{ marginBottom: 12, fontSize: 12, color: Colors.light.hereworksBlack400 }}>
        {`${users.filter((user) => user.selected).length} of ${users.length} users selected`}
      </Text>
      <View
        style={[
          styles.dropdownBox,
          { zIndex: 1, marginTop: 0, width: isMobile ? undefined : 450 },
        ]}
      >
        <TextInput
          style={[
            styles.dropdownBoxText,
            !isMobile && {
              outlineWidth: 0,
            },
          ]}
          placeholder={'Search'}
          testID='um-add-group-search-bar'
          placeholderTextColor={Colors.light.hereworksBlack400}
          onChangeText={(text) => {
            setAddGroupUserSearchValue(text);
          }}
        />
        <View style={{ flex: 1, alignItems: 'flex-end', marginRight: 15 }}>
          <SearchIcon />
        </View>
      </View>
      <View
        style={{
          flexDirection: 'row',
          borderBottomWidth: 1,
          borderColor: Colors.light.hereworksBlue100,
          alignItems: 'center',
          paddingBottom: 8,
          marginTop: 12,
          paddingLeft: 10,
        }}
      >
        {canUpdateUserGroups && <Pressable
          onPress={() => {
            const updatedUsers = [...users];
            if (users.filter((user) => user.selected).length === users.length) {
              updatedUsers.forEach((user) => {
                user.selected = false;
                user.userGroupIds?.forEach((group, index) => {
                  if (group === props.groupId) {
                    user.deleted = true;
                    user.userGroupIds.splice(index, 1);
                  }
                });
              });
            } else {
              updatedUsers.forEach((user) => {
                user.selected = true;
                user.userGroupIds?.push(props.groupId);
              });
            }
            setUsers(updatedUsers);
          }}
          testID='remove-all-btn'
        >
          <CheckBox checkcolor={users.filter((user) => user.selected).length === users.length ? Colors.light.hereworksBlue500 : 'transparent'} />
        </Pressable>}
        <Text
          style={{
            fontFamily: 'Roboto_500Medium',
            fontSize: 10,
            marginLeft: 8,
            color: Colors.light.hereworksBlack400,
            flex: 1,
          }}
        >
          NAME
        </Text>
        <Text
          style={{
            fontFamily: 'Roboto_500Medium',
            fontSize: 10,
            color: Colors.light.hereworksBlack400,
            textAlign: 'right',
            marginRight: 26,
            flex: 1,
          }}
        >
          ROLE
        </Text>
      </View>
      <View style={{ marginTop: 10 }}>
        {(users ?? [])
          .filter((user) => `${user.firstName}`.toLocaleLowerCase()
            .includes(groupUserSearchValue.toLocaleLowerCase()))
          .map((user) => (
            <View key={`${user.email}${user.selected}`}>
              <GroupUserRowMobile
                name={`${user.firstName ?? ''} ${user.lastName ?? ''}`}
                email={user.email}
                icon={user.icon}
                selected={user.selected ? true
                  : (user.userGroupIds ?? []).indexOf(props.groupId) > -1}
                setSelected={(selected) => {
                  const updatedUsers = users.map((u) => {
                    if (u.email !== user.email) return u;

                    const updatedUser = { ...u, selected };

                    if (!selected) {
                      const updatedGroupIds = (u.userGroupIds
                        ?? []).filter((g) => g !== props.groupId);
                      updatedUser.userGroupIds = updatedGroupIds;
                      updatedUser.deleted = true;
                    }

                    return updatedUser;
                  });
                  setUsers(updatedUsers);
                }}
                roleList={user.roleList}
              />
            </View>
          ))}
      </View>
    </View>
  );

  const renderSaveCancel = () => (
    <View
      style={{
        flexDirection: 'row',
        flex: isMobile ? 1 : undefined,
      }}
    >
      {isMobile ? null : <TouchableOpacity
        style={{
          backgroundColor: '#fff',
          borderRadius: 4,
          borderWidth: 1,
          borderColor: Colors.light.hereworksLightBlue,
        }}
        onPress={() => {
          logEvent('UserGroupBottomSheet', 'Cancel');
          setAddGroupUserSearchValue('');
          restoreDefaults();
        }}
      >
        <Text
          style={{
            color: Colors.light.hereworksLightBlue,
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            marginVertical: 8,
            marginHorizontal: 24,
          }}
        >
          Cancel
        </Text>
      </TouchableOpacity>}
      <TouchableOpacity
        style={{
          flex: isMobile ? 1 : undefined,
          backgroundColor: Colors.light.hereworksLightBlue,
          borderRadius: 4,
          marginLeft: isMobile ? 24 : 12,
          marginRight: isMobile ? 24 : 0,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: !saveButtonDisabled ? 1 : 0.4,
        }}
        disabled={saveButtonDisabled}
        testID='save-btn'
        onPress={save}
      >
        <Text
          style={{
            color: saving ? Colors.light.hereworksLightBlue : '#fff',
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            marginVertical: 8,
            marginHorizontal: 24,
          }}
        >
          Save
        </Text>
        {saving && <ActivityIndicator style={{ position: 'absolute' }} color="#fff" />}
      </TouchableOpacity>
    </View>
  );

  const renderModalContent = () => (
    <View
      style={{ alignSelf: 'stretch', justifyContent: 'flex-start', flex: 1 }}>
      <View
        style={{
          alignItems: 'center',
          justifyContent: 'flex-start',
          alignSelf: 'stretch',
          borderRadius: 10,
          flex: 1,
        }}
      >
        <Text
          style={{
            marginTop: 0,
            marginBottom: 14,
            fontSize: 16,
            fontFamily: 'Poppins_600SemiBold',
            color: Colors.light.hereworksBlack600,
          }}
        >
          {props.title === undefined ? 'Create group' : 'Edit group'}
        </Text>
        <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: 20 }}>
          <TabButton title="ESSENTIAL INFO"
            select={() => {
              setSelectedTab('ESSENTIAL_INFO');
            }}
            selected={selectedTab === 'ESSENTIAL_INFO'} />
          <TabButton title="MEMBERS"
            select={() => {
              setSelectedTab('MEMBERS');
            }}
            selected={selectedTab === 'MEMBERS'} />
        </View>
        <BottomSheetScrollView
          style={{
            marginHorizontal: 0,
            alignSelf: 'stretch',
            flex: 1,
          }}
          contentContainerStyle={{ paddingBottom: 12 }}
        >
          {selectedTab === 'ESSENTIAL_INFO' && renderGroupDetails()}
          {selectedTab === 'MEMBERS' && renderGroupMembers()}
        </BottomSheetScrollView>
        <View style={{
          alignSelf: 'stretch',
          flexDirection: 'row',
          marginVertical: isMobile ? 25 : 40,
          marginHorizontal: isMobile ? 0 : 40,
        }}>
          {props.title === undefined ? <></> : <View style={{ flex: isMobile ? undefined : 1 }}>
            {canDeleteUserGroups && props.title && <Pressable
              style={(state) => [{
                height: 40,
                width: 40,
                borderColor: Colors.light.hereworksRed,
                backgroundColor: state.hovered ? Colors.light.hereworksLightPink : '#fff',
                borderRadius: 4,
                borderWidth: 1,
                marginLeft: isMobile ? 24 : 0,
                alignItems: 'center',
                justifyContent: 'center',
              }]}
              testID='um-delete-user-group-btn'
              onPress={() => {
                logEvent('UserGroupBottomSheet', 'delete_group');
                if (props.notificationGroups?.length) {
                  showAlert(
                    'Cannot delete user group',
                    true,
                    <Text>
                      {'The group is currently assigned to the following template(s) in Asset management:\n'}
                      {props.notificationGroups.map((group) => group.Name).join(', ')}
                      {'\n\nPlease unassign the group from these template(s) before attempting to delete it.'}
                    </Text>,
                  );
                  return;
                }
                showAlert(
                  'Delete user group',
                  true,
                  <>
                    {'Are you sure you want to delete the user group '}
                    <Text style={{ fontFamily: 'Roboto_700Bold' }}>
                      {`${props.title ?? ''}?`}
                    </Text>
                  </>,
                  'Delete',
                  () => {
                    setSaving(true);
                    deleteUserGroup(props.groupId).then((response) => {
                      if (response && !response.isError) {
                        props.groupDeleted();
                      } else showAlert('Error', true, response?.errorText);
                    }).catch((error) => showAlert('Error', true, error.message));
                  },
                );
              }}>
              <BinIcon24 color={Colors.light.hereworksRed} />
            </Pressable>}
          </View>}
          {renderSaveCancel()}
        </View>
        <Pressable
          onPress={() => {
            logEvent('UserGroupBottomSheet', 'Close_Modal');
            restoreDefaults();
            props.close();
          }}
          testID={props.title === undefined ? 'um-close-create-user-group-modal-btn'
            : 'um-close-edit-user-group-modal-btn'}
          style={{ position: 'absolute', top: 0, right: 16 }}
        >
          <CloseModalIcon />
        </Pressable>
      </View>
    </View>
  );

  return (
    <BottomSheetModal
      index={1}
      snapPoints={['92%']}
      ref={props.userGroupSheetRef}
      enablePanDownToClose={false}
      backdropComponent={(backdropProps) => (
        <BottomSheetBackdrop
          {...backdropProps}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          pressBehavior={'none'}
        />)}
      handleIndicatorStyle={{ backgroundColor: 'transparent' }}>
        <View style={{ flex: 1 }}>
        {renderModalContent()}
          <AlertModal
            modalVisible={alertVisible}
            error={alertError}
            title={alertTitle}
            message={alertMessage}
            action={alertAction}
            actionTitle={alertActionTitle}
            setModalVisible={setAlertVisible}
          />
        </View>
    </BottomSheetModal>
  );
};

const styles = StyleSheet.create({
  noteText: {
    color: Colors.light.hereworksBlack600,
    alignSelf: 'stretch',
    fontFamily: 'Roboto_400Regular',
    alignItems: 'center',
    fontSize: 16,
    lineHeight: Platform.OS === 'ios' ? undefined : 24,
    backgroundColor: Colors.light.hereworksWhite300,
    borderWidth: 1,
    alignContent: 'center',
    marginVertical: 5,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 5,
    verticalAlign: 'top',
  },
  textBoxHeaders: {
    marginTop: 12,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    marginTop: 24,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginLeft: 10,
    flex: 1,
  },
  validationText: {
    marginTop: 4,
    color: Colors.light.hereworksBlack300,
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 20,
  },
});

export default UserGroupBottomSheet;
